import React from 'react';
import { SearchParamNames } from '../../models/enums';
import { ProductCategory, SortByValues } from '../../models/globals';
import { removeExtraWhitespace } from '../../utilities/StringUtilities';
import { SearchAction } from './reducers/SearchActions';
import { searchReducer, SearchState } from './reducers/searchReducer';
import { useBrowserSearchHistory } from './useBrowserSearchHistory';
import { useSearchAnalytics } from './useSearchAnalytics';
import { useSearchReload } from './useSearchReload';
import { useSearchResults } from './useSearchResults';

interface UseSearchProps {
    initialParams: InitialParams;
    productCategories: ProductCategory[];
    resultsApiPath: string;
}

interface InitialParams {
    [SearchParamNames.Query]: string;
    [SearchParamNames.Page]: string;
    [SearchParamNames.CourseTypes]: string[];
    [SearchParamNames.Topics]: string[];
    [SearchParamNames.SubTopics]: string[];
    [SearchParamNames.Sort]: SortByValues | undefined;
}

interface SearchEventHandlers {
    dispatch: React.Dispatch<SearchAction>;
    handleFilterChange: React.FormEventHandler<HTMLInputElement>;
    handleSearchSubmit: React.FormEventHandler<HTMLFormElement>;
}

const useSearch = ({
    initialParams,
    productCategories,
    resultsApiPath,
}: UseSearchProps): SearchState & SearchEventHandlers => {
    const [state, dispatch] = React.useReducer(searchReducer, { initialParams, productCategories }, init);
    const handleFilterChange: React.FormEventHandler<HTMLInputElement> = ({
        currentTarget: { checked, name, value },
    }) => {
        dispatch({
            type: name as SearchParamNames.CourseTypes | SearchParamNames.Topics | SearchParamNames.SubTopics,
            payload: { checked, value },
        });
    };
    const handleSearchSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
        e.preventDefault();
        const input = e.currentTarget.elements.namedItem(SearchParamNames.Query) as HTMLInputElement | null;
        dispatch({ type: SearchParamNames.Query, payload: removeExtraWhitespace(input?.value) });
    };

    useBrowserSearchHistory(state);

    useSearchResults(state, resultsApiPath, dispatch);

    useSearchReload(dispatch);

    useSearchAnalytics(state);

    return {
        ...state,
        dispatch,
        handleFilterChange,
        handleSearchSubmit,
    };
};

interface TopicMaps {
    topicMap: Record<string, { subTopics: string[] }>;
    subTopicMap: Record<string, { topic: string }>;
}

interface InitProps {
    initialParams: InitialParams;
    productCategories: ProductCategory[];
}

const init = ({ initialParams, productCategories }: InitProps): SearchState => {
    const { subTopicMap, topicMap } = productCategories.reduce<TopicMaps>(
        (map, topic) => {
            map.topicMap[topic.seoName] = {
                subTopics:
                    topic.subCategories?.map(({ seoName }) => {
                        map.subTopicMap[seoName] = {
                            topic: topic.seoName,
                        };
                        return seoName;
                    }) || [],
            };

            return map;
        },
        { subTopicMap: {}, topicMap: {} },
    );

    return {
        courseCountMap: {},
        courseTypes: new Set(initialParams[SearchParamNames.CourseTypes]),
        visibleProductTiles: [],
        isLoading: true,
        page: parseInt(initialParams[SearchParamNames.Page], 0) || 1,
        pageSize: 25,
        productTiles: [],
        searchTerms: removeExtraWhitespace(initialParams[SearchParamNames.Query]),
        subTopicMap,
        subTopics: new Set(initialParams[SearchParamNames.SubTopics]),
        topicMap,
        topics: new Set(initialParams[SearchParamNames.Topics]),
        sort: initialParams[SearchParamNames.Sort] || undefined,
        sortInUse: 'name',
    };
};

export { useSearch, InitialParams };
